import React from "react";
import { AlertCircle, Check, HelpCircle, Loader2 } from "lucide-react";
import { AvatarRing } from "../../common/comms/AvatarRing";
import {
  ROOM_FAILED,
  ROOM_SENDING,
  ROOM_SENT,
  ROOM_UNKNOWN,
} from "./bulkSendStore";

/** Stable avatar colour per client — see ChatDockUserList for the rationale. */
const colorIndexFor = (id) => (id ? String(id).charCodeAt(0) % 8 : 0);

/**
 * Wording is borrowed from the optimistic single-message bubble
 * (`MessageBubble`), so a coach who has seen one send has already learned this
 * vocabulary. `unknown` is the one addition: it says the app stopped hearing
 * about a message, which is not the same as the message failing, and the copy
 * has to point somewhere useful rather than inviting a pointless retry.
 */
const STATUS = {
  [ROOM_SENDING]: {
    label: "Sending…",
    className: "text-gray-400",
    Icon: Loader2,
    spin: true,
  },
  [ROOM_SENT]: {
    label: "Sent",
    className: "text-success",
    Icon: Check,
  },
  [ROOM_FAILED]: {
    label: "Failed to send",
    className: "text-danger",
    Icon: AlertCircle,
  },
  [ROOM_UNKNOWN]: {
    label: "Check the thread",
    className: "text-gray-400",
    Icon: HelpCircle,
  },
};

/**
 * Every recipient of an in-flight batch, with what happened to each.
 *
 * The first per-item status list in the app — both other bulk surfaces
 * (`Support/TicketList`, and this dock before this change) collapse per-row
 * outcomes into one sentence. Kept self-contained so the Support console can
 * take it later without untangling it from the dock.
 *
 * @param {Object} props
 * @param {Array<{ clientId: string, roomId: string, name: string }>} props.recipients
 * @param {Record<string, string>} props.byRoom - roomId -> status.
 * @param {Record<string, string>} [props.errors] - roomId -> server message.
 */
export function BulkProgressList({ recipients, byRoom, errors = {} }) {
  return (
    <ul className="space-y-0.5">
      {recipients.map((recipient) => {
        const status = byRoom[recipient.roomId] || ROOM_SENDING;
        const { label, className, Icon, spin } = STATUS[status] || STATUS[ROOM_SENDING];
        const error = status === ROOM_FAILED ? errors[recipient.roomId] : null;

        return (
          <li
            key={recipient.clientId}
            className="flex items-center gap-2.5 rounded-xl px-1 py-1.5"
          >
            <AvatarRing
              name={recipient.name}
              size="sm"
              colorIndex={colorIndexFor(recipient.clientId)}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium leading-tight text-gray-800">
                {recipient.name}
              </span>
              {/* The server's reason, when there is one, beats a generic
                  "failed" — "You cannot write in this room" is actionable. */}
              {error && (
                <span className="block truncate text-[11px] leading-tight text-danger">
                  {error}
                </span>
              )}
            </span>
            <span
              className={`flex flex-shrink-0 items-center gap-1 text-[11px] ${className}`}
            >
              <Icon
                aria-hidden="true"
                className={`h-3 w-3 ${spin ? "animate-spin motion-reduce:animate-none" : ""}`}
              />
              {label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default BulkProgressList;
